import * as ip from '@codegrade/ip-address';
import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import { CoursesStore, UserStore } from '@/store';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/**
 * Create a fresh initial state for the course restrictions store.
 *
 * @returns The initial state.
 */
function makeInitialState() {
    return {
        ipRangesByRestriction: {},
        entriesByRestriction: {},
    };
}
const moduleBuilder = storeBuilder.module('courseRestrictions', makeInitialState());
/** Store module for course access restriction management and student entry. */
export var CourseRestrictionsStore;
(function (CourseRestrictionsStore) {
    const commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    const commitIPRanges = moduleBuilder.commit((state, { restrictionId, newState, }) => {
        const currentState = state.ipRangesByRestriction[restrictionId];
        const deduped = {
            pagination: { tag: 'not-started' },
            ...currentState,
            ...newState,
            data: Array.from(utils.itertools.unique(item => ip.cidr.formatForHuman(item.ipRange), newState.data ?? currentState?.data ?? [])),
        };
        utils.vueSet(state.ipRangesByRestriction, restrictionId, deduped);
    }, 'commitIPRanges');
    const commitEntries = moduleBuilder.commit((state, { restrictionId, newState, }) => {
        const currentState = state.entriesByRestriction[restrictionId];
        utils.vueSet(state.entriesByRestriction, restrictionId, {
            pagination: { tag: 'not-started' },
            ...currentState,
            ...newState,
        });
    }, 'commitEntries');
    /** Getter for cached IP ranges. */
    CourseRestrictionsStore.getIPRanges = moduleBuilder.read(state => 
    /**
     * Get the cached IP ranges for a restriction.
     *
     * @param restrictionId The restriction UUID.
     * @returns A paginated state object containing the IP ranges.
     */
    (restrictionId) => utils.getPaginatedState(state.ipRangesByRestriction[restrictionId]), 'getIPRanges');
    /** Getter for cached entry overview. */
    CourseRestrictionsStore.getEntries = moduleBuilder.read(state => 
    /**
     * Get the cached entry overview for a restriction.
     *
     * @param restrictionId The restriction UUID.
     * @returns A paginated state object containing the entry overview.
     */
    (restrictionId) => utils.getPaginatedState(state.entriesByRestriction[restrictionId]), 'getEntries');
    /**
     * Update restriction settings (password, session lockdown).
     *
     * After a successful patch, the course is reloaded so
     * `course.accessRestrictions` reflects the change.
     *
     * @param root0 The arguments.
     * @param root0.restrictionId The restriction UUID.
     * @param root0.courseId The course ID to reload after patching.
     * @param root0.requestBody The patch body.
     * @returns The API result.
     */
    CourseRestrictionsStore.patchRestriction = moduleBuilder.dispatch(async (_, { restrictionId, courseId, requestBody, }) => {
        const response = await client.p.restriction.patch({
            restrictionId,
            requestBody,
        });
        // Update the course in the store with the new restriction
        // from the response, avoiding an extra network round trip.
        CoursesStore.updateCourseRestriction({
            courseId,
            restriction: response.cgResult,
        });
        return response;
    }, 'patchRestriction');
    /** Load a page of IP ranges for a restriction. */
    CourseRestrictionsStore.loadIPRanges = moduleBuilder.dispatch(async ({ state }, { 
    /** The restriction UUID. */
    restrictionId, 
    /** The pagination loading strategy. */
    toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.ipRangesByRestriction[restrictionId],
            commitData: newState => commitIPRanges({ restrictionId, newState }),
            load: async (nextToken) => await client.p.restriction.getAllAllowedIps({ restrictionId, nextToken }),
            toLoad,
        });
    }, 'loadIPRanges');
    /** Add an IP range to a restriction. */
    CourseRestrictionsStore.addIPRange = moduleBuilder.dispatch(async ({ state }, { 
    /** The restriction UUID. */
    restrictionId, 
    /** The IP range in CIDR notation. */
    ipRange, }) => {
        const response = await client.p.restriction.putAllowedIp({
            restrictionId,
            ipRange,
        });
        const currentState = state.ipRangesByRestriction[restrictionId];
        return response.addCallbacks({
            /** Commit the updated IP ranges after successful add. */
            onAfterSuccess() {
                commitIPRanges({
                    restrictionId,
                    newState: {
                        data: [
                            { ipRange: response.cgResult.ipRange },
                            ...(currentState?.data ?? []),
                        ],
                    },
                });
            },
        });
    }, 'addIPRange');
    /** Delete an IP range from a restriction. */
    CourseRestrictionsStore.deleteIPRange = moduleBuilder.dispatch(async ({ state }, { 
    /** The restriction UUID. */
    restrictionId, 
    /** The CIDR notation to delete. */
    ipRange: ipRangeToDelete, }) => {
        const response = await client.p.restriction.deleteAllowedIp({
            restrictionId,
            ipRange: ipRangeToDelete,
        });
        const current = state.ipRangesByRestriction[restrictionId];
        return response.addCallbacks({
            /** Commit the updated IP ranges after successful delete. */
            onAfterSuccess() {
                commitIPRanges({
                    restrictionId,
                    newState: {
                        data: (current?.data ?? []).filter(item => item.ipRange !== ipRangeToDelete),
                    },
                });
            },
        });
    }, 'deleteIPRange');
    /** Update an IP range (delete old, add new). */
    CourseRestrictionsStore.updateIPRange = moduleBuilder.dispatch(async ({ state }, { 
    /** The restriction UUID. */
    restrictionId, 
    /** The current CIDR notation to be deleted. */
    from, 
    /** The new CIDR notation to be added. */
    to, }) => {
        const current = state.ipRangesByRestriction[restrictionId];
        const currentData = current?.data ?? [];
        const idxItemUpdated = currentData.findIndex(item => item.ipRange === from);
        if (idxItemUpdated === -1) {
            throw new Error(`Fail to edit: cannot find the edited item "${from}" in the list. `);
        }
        // Delete then add sequentially — see assignments.ts for why
        // these cannot run in parallel (CIDR normalization race).
        await client.p.restriction.deleteAllowedIp({
            restrictionId,
            ipRange: from,
        });
        const res = await client.p.restriction.putAllowedIp({
            restrictionId,
            ipRange: to,
        });
        return res.addCallbacks({
            /** Commit ranges after successful update. */
            onAfterSuccess() {
                commitIPRanges({
                    restrictionId,
                    newState: {
                        data: [
                            ...currentData.slice(0, idxItemUpdated),
                            { ipRange: res.cgResult.ipRange },
                            ...currentData.slice(idxItemUpdated + 1),
                        ],
                    },
                });
            },
        });
    }, 'updateIPRange');
    /** Load a page of entry overview entries for a restriction. */
    CourseRestrictionsStore.loadEntries = moduleBuilder.dispatch(async ({ state }, { 
    /** The restriction UUID. */
    restrictionId, 
    /** The pagination loading strategy. */
    toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.entriesByRestriction[restrictionId],
            commitData: newState => commitEntries({ restrictionId, newState }),
            load: async (nextToken) => await client.p.restriction.getAllEntries({ restrictionId, nextToken }),
            toLoad,
        });
    }, 'loadEntries');
    const commitUpdateEntry = moduleBuilder.commit((state, { restrictionId, entry, }) => {
        const current = state.entriesByRestriction[restrictionId];
        if (current?.data != null) {
            commitEntries({
                restrictionId,
                newState: {
                    data: current.data.map(e => (e.user.id === entry.user.id ? entry : e)),
                },
            });
        }
    }, 'commitUpdateEntry');
    /** Set or remove a per-student entry override. */
    CourseRestrictionsStore.patchEntryOverride = moduleBuilder.dispatch(
    /**
     * Patch the override and update the local entry in the store.
     *
     * @param _ The store context.
     * @param root0 The arguments.
     * @returns The API result with the updated entry.
     */
    async (_, { 
    /** The restriction UUID. */
    restrictionId, 
    /** The student's user ID. */
    userId, 
    /** The override limit, or Nothing to remove the override. */
    overrideLimit, }) => {
        const result = await client.p.restriction.patchEntryOverride({
            restrictionId,
            userId,
            requestBody: { overrideLimit },
        });
        commitUpdateEntry({ restrictionId, entry: result.cgResult });
        return result;
    }, 'patchEntryOverride');
    /**
     * Enter a restricted course.
     *
     * On success, updates the user session via {@link UserStore.updateAccessToken}
     * which fires `AFTER_USER_CHANGED`, clearing all stores and resetting the
     * websocket.
     *
     * @param root0 The arguments.
     * @param root0.restrictionId The restriction UUID.
     * @param root0.password The password, or Nothing if no password is required.
     * @returns The API result.
     */
    CourseRestrictionsStore.enter = moduleBuilder.dispatch(async (_, { restrictionId, password, }) => {
        const response = await client.p.restriction.enter({
            restrictionId,
            requestBody: { password },
        });
        const loginResponse = response.cgResult;
        const isIsolated = loginResponse.restrictions.forContext?.isolated === true;
        return response.addCallbacks({
            /** Update the user session with the new token and restrictions. */
            async onAfterSuccess() {
                if (isIsolated) {
                    // Isolation bumps the session version, invalidating all
                    // previous sessions. Replace the entire stack.
                    const currentUser = UserStore.loggedInUser().unsafeCoerce();
                    await UserStore.updateAccessToken({
                        loginResponse,
                        loginMethod: currentUser.loginMethod.orDefault({
                            tag: 'username-password',
                            tenantId: Maybe.empty(),
                        }),
                        revokeOldToken: false,
                    });
                }
                else {
                    // Password-only entry: old sessions are still valid.
                    // Replace just the top session to preserve the stack.
                    await UserStore.replaceSession({
                        accessToken: loginResponse.accessToken,
                        restrictions: loginResponse.restrictions,
                        expiresAt: loginResponse.expiresAt,
                    });
                }
            },
        });
    }, 'enter');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
})(CourseRestrictionsStore || (CourseRestrictionsStore = {}));
